import deepEqual from "fast-deep-equal";
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { injectScript } from "../slices/loadStates.js";
import { useAppDispatch, useAppSelector } from "../store/hooks.js";
import { WidgetInstance } from "../types/index.js";
import LazyloadWrapper from "./LazyloadWrapper.js";

interface Props {
  widgetInstance: WidgetInstance | undefined;
  settings: {};
  initWidget: () => void;
  enableLazyload: boolean;
  children: ReactNode;
}

const bsWidgetScriptUrl = "https://widget.bookingsuedtirol.com/v2/bundle.js";

const HgvWidgetContainer: FunctionComponent<Props> = ({
  widgetInstance,
  children,
  settings,
  initWidget,
  enableLazyload,
}) => {
  const dispatch = useAppDispatch();

  const loadStatus = useAppSelector(
    (state) => state.loadStates.scripts[bsWidgetScriptUrl] ?? "unloaded",
  );

  const prevSettingsRef = useRef(settings);
  const [isLazyLoaded, setIsLazyloaded] = useState(false);
  const isReadyToLoad = isLazyLoaded || !enableLazyload;

  useEffect(() => {
    isReadyToLoad && dispatch(injectScript(bsWidgetScriptUrl));
  }, [isReadyToLoad]);

  useEffect(() => {
    loadStatus === "loaded" && isReadyToLoad && initWidget();
  }, [loadStatus, isReadyToLoad]);

  // Remount if settings change
  useEffect(() => {
    if (loadStatus !== "loaded" || !isReadyToLoad) return;

    const settingsAreEqual = deepEqual(settings, prevSettingsRef.current);
    prevSettingsRef.current = settings;
    if (settingsAreEqual) return;
    widgetInstance?.unmount();
    initWidget();
  }, [settings, widgetInstance, loadStatus, isReadyToLoad]);

  return <LazyloadWrapper onLoad={setIsLazyloaded}>{children}</LazyloadWrapper>;
};

export default HgvWidgetContainer;
